import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { getAdminConfiguration, putAdminConfiguration } from '../apis/adminApi';
import { PurgeJobs } from "../components/Admin/Purge";

export const AdminPage = () => 
{
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);
    
    const [adminConfig, setAdminConfig] = useState(null);
    const [alert, setAlert] = useState(null);
    const [authorized, setAuthorized] = useState(true);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const formattingSettingsName = useCallback((settingsName) => {
        return settingsName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^./, str => str.toUpperCase());
    }, []);

    const handleInputChange = useCallback((e, settingKey, settingType) => {
        let updatedValue = e.target.value;

        if (settingType === 'number') { 
            updatedValue = Math.max(0, parseInt(updatedValue) || 0);
        } else if (settingType === 'date') {
            updatedValue = updatedValue < today ? today : updatedValue;
        } else if (settingType === 'boolean') {
            updatedValue = Boolean(e.target.checked);
        }
       console.log(e.target.name);
        setAdminConfig(prevConfig => ({
            ...prevConfig,
            [settingKey]: {
                ...prevConfig[settingKey],
                [e.target.name]: updatedValue
            }
        }));
        
        setErrors(prev =>{
            const mod = {...prev};
            delete mod[settingKey];
            return mod;
        });
    });


    useEffect(() => {
        async function fetchAdminConfig() {
            try {
                const response = await getAdminConfiguration();
                
                if (response.config === null) {
                    setAlert({ message: response.message, variant: "warning" });
                } else if (Object.keys(response.config).length === 0) {
                    setAlert({ message: "No Configuration Settings Are Currently Available.", variant: "info" });
                } else {
                    setAdminConfig(response.config);
                }
            } catch (error) { 
                if (error.status === 403) {
                    setAuthorized(false);
                } else {
                    setAlert({ message: "Failed To Fetch Admin Configuration. Please Try Again Later.", variant: "danger" });
                }
            } finally {
                setLoading(false);
            }
        }
        fetchAdminConfig(); 
    }, []);
    
    const validateConfig = useCallback((config) => {
        const fieldErrors = {};
        const settings = adminConfig[config];
        Object.entries(settings).forEach(([key, value]) => {
            if (typeof value === 'number') {
                if (!Number.isInteger(value) || value < 0) {
                    fieldErrors[key] = 'Must be a whole number (0 or greater).';
                }
            } else if (typeof value === 'string') {
                if (value.trim() === '') {
                    fieldErrors[key] = 'This field cannot be empty.';
                }
            }
        });
        return fieldErrors;
    }, [adminConfig]);

    const updateAdminConfiguration = useCallback(async (e, config) => {
        e.preventDefault();
        const fieldErrors = validateConfig(config);
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(prev => ({ ...prev, [config]: fieldErrors }));
            return;
        }
        setErrors(prev => ({ ...prev, [config]: {} }));
        setSubmitting(true);
        try {
            await putAdminConfiguration(adminConfig);
            setAlert({ message: `${formattingSettingsName(config)} updated successfully!`, variant: "success" });
        } catch (error) {
            setAlert({ message: `Failed to update ${formattingSettingsName(config)}. Please try again.`, variant: "danger" });
        } finally {
            setSubmitting(false);
        }
    }, [adminConfig, formattingSettingsName, validateConfig]);

    if (!authorized) {
        return (
            <Alert variant="danger">
                You do not have permission to access settings.
            </Alert>
        );
    }

    if (loading) {
        return (
            <div className="admin-header text-center">
                <Spinner animation="border" role="status" />
                <h3 className="display-5 mt-3">Loading Settings...</h3>
            </div>
        );
    }

    return (
        <>
            {alert && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}
            {adminConfig ? (
                <Container fluid className="p-5 rounded-3">
                    {Object.entries(adminConfig).map(([config, settings]) => (
                        <Form key={config} onSubmit={e => updateAdminConfiguration(e, config)}>
                            <Row>
                                <h4>{formattingSettingsName(config)}</h4>
                                <Row>
                                    {Object.entries(settings).map(([settingKey, setting]) => (
                                        <Col lg={6} md={12} sm={12} key={settingKey}>
                                            <Form.Group className="mb-3" controlId={`${config}-${settingKey}`}>
                                                <div className="admin-form">
                                                    <Col>
                                                        <Form.Label>{formattingSettingsName(settingKey)}</Form.Label>
                                                    </Col>
                                                    <Col>
                                                        {typeof setting === 'boolean' ? (
                                                            <Form.Check
                                                                type="switch"
                                                                name={settingKey}
                                                                checked={setting}
                                                                onChange={e => handleInputChange(e, config, typeof setting)}
                                                            />
                                                        ) : (
                                                            <>
                                                            <Form.Control
                                                                required
                                                                name={settingKey}
                                                                className="admin-form-control"
                                                                type={typeof setting}
                                                                isInvalid={!!errors[config]?.[settingKey]}
                                                                min={typeof setting === 'number' ? 0 : undefined}
                                                                onKeyDown={typeof setting === 'number' ? (e) => {
                                                                    if (e.key === 'e' || e.key === 'E' || e.key === '.' || e.key === ',') {
                                                                        e.preventDefault();
                                                                        setErrors(prev => ({
                                                                            ...prev,
                                                                            [config]: { ...prev[config], [settingKey]: 'Only whole numbers are allowed.' }
                                                                        }));
                                                                    }
                                                                } : undefined}
                                                                onInput={e => handleInputChange(e, config, typeof setting)}
                                                                defaultValue={setting}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {errors[config]?.[settingKey]}
                                                            </Form.Control.Feedback>
                                                            </>
                                                        )}
                                                    </Col>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    ))}
                                </Row>
                            </Row>
                            <Row>
                                <Col lg={{ offset: 11, span: 1 }} md={{ offset: 10, span: 2 }} sm={{ offset: 8, span: 2 }}>
                                    <Button className="button-table" type="submit" disabled={submitting}>
                                        {submitting ? <Spinner size="sm" /> : 'Submit'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    ))}
                    <hr/>
                    <PurgeJobs />
                </Container>
            ) : null}
        </>
    );
}